import { z } from "zod";
import {
  VENDOR_STATUS_OPTIONS,
  RESIDENTIAL_STATUS_OPTIONS,
  GROSSING_UP_OPTIONS,
  NATURE_OF_REMITTANCE_OPTIONS,
  TDS_RATE_BELONGS_TO_OPTIONS,
  REMARKS_OPTIONS,
  MONTH_OPTIONS,
} from "../../../constants/index.js";

// Helper to extract enum values from options
const getEnumValues = (options) => options.map((opt) => opt.value);

// 🔹 Normalize string inputs
const asString = z.preprocess(
  (val) => (val !== null && val !== undefined ? String(val) : val),
  z.string().optional().nullable()
);

const alphanumericWithMax = (max) =>
  asString.refine(
    (val) => !val || /^[\w\s]*$/.test(val) && val.length <= max,
    `Must be alphanumeric and up to ${max} characters`
  );

// 🔹 Normalize number inputs
const asNumber = z.preprocess(
  (val) => (val !== null && val !== undefined && val !== "" ? Number(val) : val),
  z.number().optional().nullable()
);

// 🔹 Enum with capitalization normalization
const asEnum = (options) =>
  z.preprocess(
    (val) => (typeof val === "string" ? val.trim() : val),
    z.enum(getEnumValues(options)).optional().nullable()
  );


// Capitalize helper
function capitalize(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// Numeric with 12 digits and 2 decimals
const numeric12_2 = asString.refine(
  (val) => !val || /^\d{1,10}(\.\d{1,2})?$/.test(val),
  "Must be numeric with up to 12 digits and 2 decimals"
);

const commonEditableSchema = {
  entityTAN: asString,
  vendorPAN: asString,
  vendorName: z
    .string()
    .max(50, "Vendor Name must not exceed 50 characters")
    .optional()
    .nullable(),
  year: asString,
  month: asNumber,
  postingDate: z.string().min(1, "Posting Date is required"),
  transactionCategory: asString,
  invoiceNo: asString,
  invoiceDate: asString,
  accountingDocumentNo: asString,
  accountingDocumentDate: asString,
  referenceNo: z
    .string()
    .max(50, "Reference No. must not exceed 50 characters")
    .optional()
    .nullable(),
  dateOfDeduction: asString,
  grossExpenseAmount: numeric12_2,
  tdsBaseAmount: numeric12_2,
  tdsSection: z
    .string()
    .max(5, "TDS Section must not exceed 5 characters")
    .optional()
    .nullable(),
  tdsRate: z.string().optional().nullable().refine(
    (val) => !val || /^\d{1,3}(\.\d{1,2})?$/.test(val),
    "Must be numeric with up to 3 digits and 2 decimals"
  ),
  tdsAmount: numeric12_2,
  surcharge: numeric12_2,
  educationCess: numeric12_2,
  totalTdsLiability: numeric12_2,
  interest: numeric12_2,
  others: numeric12_2,
  poDescription: alphanumericWithMax(500),
  narration: alphanumericWithMax(500),
  expenseGlDescription: alphanumericWithMax(500),
  invoiceDescription: alphanumericWithMax(500),
  hsnCode: z
    .string()
    .optional()
    .nullable()
    .refine(
      (val) => !val || /^\d{1,8}$/.test(val),
      "Must be numeric with up to 8 digits"
    ),
  glCode: alphanumericWithMax(100),

  vendorStatus: asEnum(VENDOR_STATUS_OPTIONS),
  residentialStatus: asEnum(RESIDENTIAL_STATUS_OPTIONS),
  grossingUpIndicator: asEnum(GROSSING_UP_OPTIONS),
  natureOfRemittance: z.enum(getEnumValues(NATURE_OF_REMITTANCE_OPTIONS)).optional().nullable(),
  tdsRateBelongsTo: asEnum(TDS_RATE_BELONGS_TO_OPTIONS),
  remarks: asEnum(REMARKS_OPTIONS),

  certificateNumber: alphanumericWithMax(10),
  challanSerialNo: asString
    .optional()
    .nullable()
    .refine(
      (val) => !val || /^\d{1,9}$/.test(val),
      "Must be numeric with up to 9 digits"
  ),
  bsrCode: asString
    .optional()
    .nullable()
    .refine(
      (val) => !val || /^\d{1,7}$/.test(val),
      "Must be numeric with up to 7 digits"
  ),
  dateOfTaxDeposited: asString,
  totalTaxDeposited: numeric12_2,
  paidByBookEntry: asString,
  accountingYear: asString,
  accountingMonth: asNumber,
  vendorCountry: asString,
  vendorEmail: z.string().email("Invalid email").or(z.literal("")).optional().nullable(),
  vendorContact: z.string().optional()
    .nullable()
    .refine(
      (val) => !val || /^\d{10,15}$/.test(val),
      "Vendor Contact must be numeric and 10 to 15 digits"
    ),
  vendorAddress: asString.refine(
    (val) => !val || (!/\^/.test(val) && !/"/.test(val) && val.length <= 150),
    "Vendor Address must not contain ^ or \" and max 150 characters"
  ),
  vendorReferenceNo: asString,
  tinUin: asString,

  // MIS1 → MIS20
  ...Object.fromEntries(
    Array.from({ length: 20 }).map((_, i) => [`mis${i + 1}`, alphanumericWithMax(200)])
  ),

  // FVU1 → FVU20
  ...Object.fromEntries(
    Array.from({ length: 20 }).map((_, i) => [`fvu${i + 1}`, alphanumericWithMax(200)])
  ),
};

// Extra editable fields for unprocessed tab
const unprocessedExtraEditable = {
  errorCount: asNumber,
  errorList: z
    .array(z.object({ errorMessage: asString }))
    .optional()
    .nullable(),
};

// Function to get editable schema per tab
export const getEditableSchemaForTab = (tabStatus) => {
  let baseSchema;

  switch (tabStatus) {
    case "unprocessed":
      baseSchema = z.object({ ...commonEditableSchema, ...unprocessedExtraEditable });
      break;
    default:
      baseSchema = z.object({ ...commonEditableSchema });
      break;
  }

  return baseSchema.superRefine((data, ctx) => {
    // Non-resident validations
    if (
      data.residentialStatus === "NR" &&
      (!data.vendorEmail || data.vendorEmail.trim() === "")
    ) {
      ctx.addIssue({
        path: ["vendorEmail"],
        code: z.ZodIssueCode.custom,
        message: "Vendor Email is mandatory for Non-Resident",
      });
    }
    if (
      data.residentialStatus === "NR" &&
      (!data.vendorContact || data.vendorContact.trim() === "")
    ) {
      ctx.addIssue({
        path: ["vendorContact"],
        code: z.ZodIssueCode.custom,
        message: "Vendor Contact number is required for Non-Resident",
      });
    }

    // Entity TAN
    if (!data.entityTAN || data.entityTAN.trim() === "") {
      ctx.addIssue({
        path: ["entityTAN"],
        code: z.ZodIssueCode.custom,
        message: "Entity TAN is required",
      });
    }

   // InvoiceNo or AccountingDocumentNo → at least one required
    if (
      (!data.invoiceNo || data.invoiceNo.trim() === "") &&
      (!data.accountingDocumentNo || data.accountingDocumentNo.trim() === "")
    ) {
      ctx.addIssue({
        path: ["invoiceNo"], // you can also point to both fields if needed
        code: z.ZodIssueCode.custom,
        message:
          "Both 'Invoice No.' and 'Accounting Document No.' cannot be blank. Provide a value in either column.",
      });
    }
    // GrossExpenseAmount or TDSBaseAmount → at least one required
    if (
      (!data.grossExpenseAmount || data.grossExpenseAmount.trim() === "") &&
      (!data.tdsBaseAmount || data.tdsBaseAmount.trim() === "")
    ) {
      ctx.addIssue({
        path: ["grossExpenseAmount"],
        code: z.ZodIssueCode.custom,
        message:
          'Both "Gross Expense Amount" & "TDS Base Amount" cannot be kept blank. Please enter a value in either column.',
      });
    }

  });
};
